/**
 * HR → «Премии и штрафы» (05.09, владелец: «ОО в таб HR нужен новый субтаб
 * премии/штрафы»). Одна лента за период шапочного календаря (как SalaryTab):
 * премии — GET /api/bonuses (var(--blue)), штрафы — GET /api/penalties
 * (var(--red)), по period_date DESC. Жизненный цикл у обоих draft → sent →
 * cancelled: черновик — «Отправить» (POST /:id/send) / «Удалить» (DELETE),
 * отправлено — «Отменить» (POST /:id/cancel) с инлайн-подтверждением «точно?
 * да/нет» (A3: native confirm запрещён), отменено — приглушено, без действий.
 * Защита от двойного тапа — busy по ключу kind:id. Выдача — BonusPenaltySheet.
 * 26.10 (владелец): тихие строки помечены «🔕 без уведомления» (только офису);
 * отметку в расчётке можно поставить и снять у неотменённой строки — без сообщений.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../api/client";
import type { BoardOperator } from "../../hooks/useOoDay";
import type { DateRange } from "../../components/DateSelector";
import { todayBusiness, toBusinessDate, toUtc5, addDays, fmtDateApi } from "../../utils/date";
import { BonusPenaltySheet, errText, type Kind } from "./BonusPenaltySheet";
import { TextPad } from "../../components/shared";
import { SlipMarkTag } from "../../components/shared/SlipMark";
import { SLIP_MARK_MAX, hasSlipMark } from "@ub/shared/slipMark";
import board from "./board.module.css";
import css from "./bonusPenalty.module.css";

type Status = "draft" | "sent" | "cancelled";
interface Row {
  id: number; operator_id: string; operator_name?: string | null; amount: number; reason: string; period_date: string;
  status: Status; issued_by_name?: string | null; cancelled_by_name?: string | null; sent_at?: string | null;
  /** 26.10: премии отдают boolean, штрафы — 0/1 из колонки */ silent?: boolean | number | null; slip_mark?: string | null;
}
interface Item extends Row { kind: Kind }
type Act = "send" | "cancel" | "delete";

const BASE: Record<Kind, string> = { bonus: "/api/bonuses", penalty: "/api/penalties" };
const fmtR = (n: number) => `₽${String(Math.round(n || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, " ")}`;
const dm = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;
/** sent_at из D1 (datetime('now'), UTC) → «дд.мм чч:мм» в бизнес-зоне UTC+5. */
function dmhm(s: string): string {
  const d = new Date(`${s.replace(" ", "T")}Z`);
  if (Number.isNaN(d.getTime())) return s;
  const u = toUtc5(d);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(u.getUTCDate())}.${p(u.getUTCMonth() + 1)} ${p(u.getUTCHours())}:${p(u.getUTCMinutes())}`;
}
function statusText(it: Item): string {
  if (it.status === "sent") return `отправлено${it.sent_at ? ` ${dmhm(it.sent_at)}` : ""}`;
  if (it.status === "cancelled") return `отменено${it.cancelled_by_name ? ` — ${it.cancelled_by_name}` : ""}`;
  return "черновик";
}
const DONE: Record<Act, string> = { send: "Отправлено оператору", cancel: "Отменено", delete: "Черновик удалён" };
const quiet = (it: Item) => it.silent === true || it.silent === 1;

export function BonusPenaltyTab({ operators, range }: { operators: BoardOperator[]; range: DateRange }) {
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [ask, setAsk] = useState<string | null>(null);
  const [sheetKind, setSheetKind] = useState<Kind>("bonus");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [toast, setToast] = useState<{ msg: string; err: boolean } | null>(null);
  const [markFor, setMarkFor] = useState<Item | null>(null);   // 26.10: подпись отметки — TextPad
  const aliveRef = useRef(true);
  const busyRef = useRef(false); // синхронный гейт: два тапа до ре-рендера видят один и тот же state
  const toastTimer = useRef(0);
  useEffect(() => { aliveRef.current = true; return () => { aliveRef.current = false; window.clearTimeout(toastTimer.current); }; }, []);
  const flash = (msg: string, isErr = false) => {
    setToast({ msg, err: isErr });
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => { if (aliveRef.current) setToast(null); }, 3200);
  };
  const reload = () => setTick((n) => n + 1);

  /* период — ШАПОЧНЫЙ календарь экрана (паттерн SalaryTab); пока не выбран —
     текущая зарплатная неделя пт–чт */
  const period = useMemo(() => {
    if (range?.start) {
      const from = fmtDateApi(toBusinessDate(range.start));
      return { from, to: range.end ? fmtDateApi(toBusinessDate(range.end)) : from };
    }
    const t = todayBusiness();
    const start = addDays(t, -((t.getUTCDay() - 5 + 7) % 7));
    return { from: fmtDateApi(start), to: fmtDateApi(addDays(start, 6)) };
  }, [range]);

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(null); setAsk(null);
    const q = `?from=${period.from}&to=${period.to}`;
    const one = (kind: Kind) => api<{ results?: Row[] }>(`${BASE[kind]}${q}`).then((r) => (r.results ?? []).map((x): Item => ({ ...x, kind })));
    void Promise.allSettled([one("bonus"), one("penalty")]).then((rs) => {
      if (!alive) return;
      const next: Item[] = [];
      const fails: string[] = [];
      rs.forEach((r, i) => { if (r.status === "fulfilled") next.push(...r.value); else fails.push(i === 0 ? "премии" : "штрафы"); });
      next.sort((a, b) => b.period_date.localeCompare(a.period_date) || b.id - a.id);
      setItems(next);
      setErr(fails.length ? `Не загрузились: ${fails.join(", ")}` : null);
      setLoading(false);
    });
    return () => { alive = false; };
  }, [period, tick]);

  const key = (it: Item) => `${it.kind}:${it.id}`;
  const run = async (it: Item, act: Act) => {
    if (busyRef.current) return;
    busyRef.current = true;
    const k = key(it);
    setBusy(k); setAsk(null);
    try {
      if (act === "delete") await api(`${BASE[it.kind]}/${it.id}`, { method: "DELETE", retries: 0 });
      else await api(`${BASE[it.kind]}/${it.id}/${act}`, { method: "POST", retries: 0 });
      if (aliveRef.current) { flash(act === "send" && quiet(it) ? "Начислено без уведомления" : DONE[act]); reload(); }
    } catch (e) { if (aliveRef.current) flash(errText(e), true); } finally { busyRef.current = false; if (aliveRef.current) setBusy(null); }
  };
  /* 26.10: отметка в расчётке — PUT одной лишь slip_mark (true — «★» без подписи, false — снять); сервер не пишет никому */
  const setMark = async (it: Item, mark: string | boolean) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(key(it)); setAsk(null);
    try {
      await api(`${BASE[it.kind]}/${it.id}`, { method: "PUT", body: { slip_mark: mark }, retries: 0 });
      if (aliveRef.current) { flash(mark === false ? "Отметка в расчётке снята" : "Отмечено в расчётке"); reload(); }
    } catch (e) { if (aliveRef.current) flash(errText(e), true); } finally { busyRef.current = false; if (aliveRef.current) setBusy(null); }
  };
  const markBtn = (it: Item) => (hasSlipMark(it.slip_mark)
    ? <button type="button" className={css.act} disabled={!!busy} onClick={() => void setMark(it, false)}>Снять ★</button>
    : <button type="button" className={css.act} disabled={!!busy} onClick={() => setMarkFor(it)}>★ В расчётку</button>);

  const sent = items.filter((it) => it.status === "sent");
  const sum = (kind: Kind) => sent.filter((it) => it.kind === kind).reduce((a, it) => ({ n: a.n + 1, r: a.r + (it.amount || 0) }), { n: 0, r: 0 });
  const bs = sum("bonus"), ps = sum("penalty");
  const openSheet = (kind: Kind) => { setSheetKind(kind); setSheetOpen(true); };

  return (
    <div className={board.content}>
      <div className={css.head}>
        <div className={css.sum}>
          <span className={css.sumBonus}>🎁 Премий: {bs.n} · <b>{fmtR(bs.r)}</b></span>
          <span className={css.sumPen}>⚠️ Штрафов: {ps.n} · <b>−{fmtR(ps.r)}</b></span>
        </div>
        <div className={css.btns}>
          <button type="button" className={`${css.add} ${css.addBonus}`} onClick={() => openSheet("bonus")}>+ Премия</button>
          <button type="button" className={`${css.add} ${css.addPen}`} onClick={() => openSheet("penalty")}>+ Штраф</button>
        </div>
      </div>
      {err ? <div className={css.hint}>{err}</div> : null}
      {loading ? <div className={board.empty}>Загружаю…</div> : items.length === 0 ? <div className={board.empty}>За период ничего нет</div> : (
        <div className={css.list}>
          {items.map((it) => {
            const k = key(it);
            const isPen = it.kind === "penalty";
            const wait = busy === k;
            return (
              <div key={k} className={`${css.row} ${isPen ? css.rowPen : ""} ${it.status === "cancelled" ? css.rowOff : ""}`}>
                <div className={css.top}>
                  <span className={css.badge}>{isPen ? "⚠️" : "🎁"}</span>
                  <span className={css.date}>{dm(it.period_date)}</span>
                  <span className={css.name}>{it.operator_name || it.operator_id}</span>
                  <span className={`${css.amt} ${isPen ? css.amtPen : css.amtBonus}`}>{isPen ? "−" : ""}{fmtR(it.amount)}</span>
                </div>
                <div className={css.reason}><SlipMarkTag mark={it.slip_mark} tone={isPen ? "pen" : "bonus"} />{it.reason}</div>
                <div className={css.meta}>
                  <span className={it.status === "sent" ? css.stSent : ""}>{statusText(it)}</span>
                  {it.issued_by_name ? ` · выдал: ${it.issued_by_name}` : ""}
                  {quiet(it) ? <span className={css.quiet}> · 🔕 без уведомления</span> : null}
                </div>
                {it.status === "draft" ? (
                  <div className={css.acts}>
                    <button type="button" className={`${css.act} ${css.actGo}`} disabled={!!busy} onClick={() => void run(it, "send")}>{wait ? "…" : "Отправить"}</button>
                    <button type="button" className={css.act} disabled={!!busy} onClick={() => void run(it, "delete")}>Удалить</button>
                    {markBtn(it)}
                  </div>
                ) : it.status === "sent" ? (
                  <div className={css.acts}>
                    {ask === k ? (
                      <span className={css.ask}>Точно отменить?
                        <button type="button" className={css.yes} disabled={!!busy} onClick={() => void run(it, "cancel")}>да</button>
                        <button type="button" className={css.no} disabled={!!busy} onClick={() => setAsk(null)}>нет</button>
                      </span>
                    ) : <button type="button" className={`${css.act} ${css.actNo}`} disabled={!!busy} onClick={() => setAsk(k)}>{wait ? "…" : "Отменить"}</button>}
                    {ask === k ? null : markBtn(it)}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
      <BonusPenaltySheet open={sheetOpen} kind={sheetKind} operators={operators} onClose={() => setSheetOpen(false)}
        onDone={(msg) => { setSheetOpen(false); flash(msg); reload(); }} />
      <TextPad open={!!markFor} label="Подпись отметки · необязательно" value="" maxLength={SLIP_MARK_MAX} placeholder="например: KPI сентябрь"
        onCommit={(v) => { const it = markFor; if (it) void setMark(it, v.replace(/\s+/g, " ").trim() || true); }} onClose={() => setMarkFor(null)} />
      {toast ? <div className={`${css.toast} ${toast.err ? css.toastErr : ""}`}>{toast.msg}</div> : null}
    </div>
  );
}
